import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import clienteService, { Cliente } from '../../../services/clienteService';
import { getFileNameFromContentDisposition } from '../../../utils/contentDisposition';
import { loadInfo360Tab, saveInfo360Tab } from '../../../utils/info360TabStorage';
import PDFViewer from '../../../components/shared/PDFViewer';
import type { Info360ActividadItem, Info360Documento, Info360Indicadores, Info360Nota } from './types';
import type { BcraCentralDeudoresConsulta } from '../../../types/bcraCentralDeudores';
import { resolverAccionDocumento } from './documentoAcciones';
import Info360KpiRow from './components/Info360KpiRow';
import Info360Recommendation from './components/Info360Recommendation';
import Info360BcraStrip from './components/Info360BcraStrip';
import Info360TabResumen from './components/Info360TabResumen';
import Info360TabCredito from './components/Info360TabCredito';
import Info360TabVentas from './components/Info360TabVentas';
import Info360TabActividad from './components/Info360TabActividad';
import Info360NotasBloque from './components/Info360NotasBloque';

const CLIENTE_TABS = ['resumen', 'credito', 'ventas', 'actividad'] as const;
type TabKey = (typeof CLIENTE_TABS)[number];

interface PdfActivo {
  url: string;
  titulo: string;
  documentId: string;
  documentType: 'recibo' | 'devolucion';
  fileName?: string;
}

interface Props {
  nroCliente: number;
  /** Encabezado propio de la pantalla o del diálogo. */
  renderHeader?: (cliente: Cliente) => React.ReactNode;
  /** En un documento en curso, evita perder lo cargado abriendo la consulta en otra pestaña. */
  abrirDocumentosEnNuevaPestana?: boolean;
  accionSecundariaError?: { label: string; onClick: () => void };
}

const Info360Contenido: React.FC<Props> = ({
  nroCliente,
  renderHeader,
  abrirDocumentosEnNuevaPestana = false,
  accionSecundariaError,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user, tenantId } = useAuth();

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [indicadores, setIndicadores] = useState<Info360Indicadores | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>(() =>
    loadInfo360Tab('cliente', user?.username, tenantId, CLIENTE_TABS, 'resumen'),
  );
  const [pdf, setPdf] = useState<PdfActivo | null>(null);
  const [notas, setNotas] = useState<Info360Nota[]>([]);
  const [notasLoading, setNotasLoading] = useState(false);
  const [notasError, setNotasError] = useState<string | null>(null);
  const [bcraData, setBcraData] = useState<BcraCentralDeudoresConsulta | null>(null);
  const [bcraLoading, setBcraLoading] = useState(false);
  const pdfUrlRef = useRef<string | null>(null);

  const cargar = useCallback(async () => {
    if (!Number.isFinite(nroCliente) || nroCliente <= 0) {
      setCliente(null);
      setIndicadores(null);
      setError('Número de cliente inválido.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setNotasLoading(true);
    setNotasError(null);
    try {
      const [clienteData, info360Data] = await Promise.all([
        clienteService.getCliente(nroCliente, { soloOperativo: false }),
        clienteService.getClienteInfo360(nroCliente),
      ]);
      setCliente(clienteData);
      setIndicadores(info360Data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 404
        ? `No se encontró el cliente #${nroCliente}.`
        : 'No se pudo cargar la información del cliente. Intentá nuevamente.');
      setCliente(null);
      setIndicadores(null);
    } finally {
      setLoading(false);
    }

    try {
      const notasData = await clienteService.getNotasOperativasComerciales(nroCliente);
      setNotas(notasData);
    } catch {
      setNotasError('No se pudieron cargar las notas del cliente.');
      setNotas([]);
    } finally {
      setNotasLoading(false);
    }
  }, [nroCliente]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => () => {
    if (pdfUrlRef.current) window.URL.revokeObjectURL(pdfUrlRef.current);
  }, []);

  const abrirDocumento = useCallback(async (
    documento: Info360Documento | Info360ActividadItem,
    etiqueta: string,
  ) => {
    const accion = resolverAccionDocumento(documento);
    if (!accion) return;

    if (accion.kind === 'ruta') {
      if (abrirDocumentosEnNuevaPestana) {
        window.open(accion.path, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(accion.path);
      return;
    }

    try {
      const response = await api.get(accion.url, { responseType: 'blob' });
      const contentDisposition = (response.headers as Record<string, string | undefined>)?.['content-disposition'];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      if (pdfUrlRef.current) window.URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = url;
      setPdf({
        url,
        titulo: `${accion.titulo} ${etiqueta}`.trim(),
        documentId: String(documento.nroFormulario ?? ''),
        documentType: accion.documentType,
        fileName: getFileNameFromContentDisposition(contentDisposition) ?? undefined,
      });
    } catch {
      enqueueSnackbar(`No se pudo generar el PDF del comprobante ${etiqueta}.`, { variant: 'error' });
    }
  }, [abrirDocumentosEnNuevaPestana, enqueueSnackbar, navigate]);

  const handleAbrirDocumento = useCallback((documento: Info360Documento) => {
    void abrirDocumento(documento, documento.numero);
  }, [abrirDocumento]);

  const handleAbrirActividad = useCallback((actividad: Info360ActividadItem) => {
    void abrirDocumento(actividad, actividad.descripcion);
  }, [abrirDocumento]);

  const cerrarPdf = () => {
    if (pdfUrlRef.current) window.URL.revokeObjectURL(pdfUrlRef.current);
    pdfUrlRef.current = null;
    setPdf(null);
  };

  if (loading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
        <CircularProgress />
        <Typography color="text.secondary">Cargando Info 360…</Typography>
      </Stack>
    );
  }

  if (error || !cliente || !indicadores) {
    return (
      <Stack spacing={2} sx={{ py: 2 }}>
        <Alert severity="error">{error || 'No hay datos para mostrar.'}</Alert>
        <Stack direction="row" spacing={1}>
          {accionSecundariaError ? (
            <Button
              variant="outlined"
              onClick={accionSecundariaError.onClick}
              sx={{ textTransform: 'none' }}
            >
              {accionSecundariaError.label}
            </Button>
          ) : null}
          <Button variant="contained" onClick={() => void cargar()} sx={{ textTransform: 'none' }}>
            Reintentar
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      {renderHeader?.(cliente)}

      <Info360KpiRow data={indicadores} />
      <Info360BcraStrip
        cuit={cliente.cuit}
        onBcraData={setBcraData}
        onLoadingChange={setBcraLoading}
      />
      <Info360Recommendation
        recomendacion={indicadores.recomendacion}
        alertas={indicadores.alertas}
      />

      <Info360NotasBloque notas={notas} loading={notasLoading} error={notasError} />

      <Box>
        <Tabs
          value={tab}
          onChange={(_, value: TabKey) => {
            setTab(value);
            saveInfo360Tab('cliente', user?.username, tenantId, value);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Secciones Info 360"
        >
          <Tab label="Resumen" value="resumen" />
          <Tab label="Crédito y cobranzas" value="credito" />
          <Tab label="Ventas" value="ventas" />
          <Tab label="Actividad" value="actividad" />
        </Tabs>
      </Box>

      <Box role="tabpanel">
        {tab === 'resumen' ? (
          <Info360TabResumen
            cliente={cliente}
            data={indicadores}
            onAbrirDocumento={handleAbrirDocumento}
          />
        ) : null}
        {tab === 'credito' ? (
          <Info360TabCredito
            nroCliente={nroCliente}
            data={indicadores}
            bcraData={bcraData}
            bcraLoading={bcraLoading}
            onAbrirDocumento={handleAbrirDocumento}
          />
        ) : null}
        {tab === 'ventas' ? (
          <Info360TabVentas data={indicadores} onAbrirDocumento={handleAbrirDocumento} />
        ) : null}
        {tab === 'actividad' ? (
          <Info360TabActividad
            data={indicadores}
            onAbrirDocumento={handleAbrirDocumento}
            onAbrirActividad={handleAbrirActividad}
          />
        ) : null}
      </Box>

      {pdf ? (
        <PDFViewer
          pdfUrl={pdf.url}
          onClose={cerrarPdf}
          title={pdf.titulo}
          documentId={pdf.documentId}
          documentType={pdf.documentType}
          downloadFileName={pdf.fileName}
        />
      ) : null}
    </Stack>
  );
};

export default Info360Contenido;
